/**
 * The staff quote page's file column (the journeys branch's round 18 mock-up;
 * docs/features/order-to-invoice/66_views_and_frontend_artwork_update_spec.md
 * §3): under each line, one rail row per file the customer or we uploaded on
 * it, with the check's result in the customer's words and the staff detail in
 * its ⓘ, and a row for each part still without a file. Files are optional
 * until the order; proofs wait for it.
 *
 * Every sentence and URL is the read's. A step shows only where the read
 * carries its URL.
 */
import type {
  StaffArtworkLine,
  StaffPart,
  StaffVersionArtwork,
} from "@shared-core/data/artwork-staff-types";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import { RailRow } from "../../patterns/LifecycleRail";
import { ViewDetail } from "../orders/StepPanelParts";
import { SupplyGuide } from "./SupplyGuide";

const short = formatShortDateOrDash;

/** A part's row tone: a verdict that needs no one done, can't print a problem, a held or running check in progress. */
function nodeOfPart(part: StaffPart) {
  switch (part.verdict) {
    case null:
      return { state: "to_do" as const, problem: null };
    case "passed":
    case "we_will_sort":
    case "worth_knowing":
      return { state: "done" as const, problem: null };
    case "cant_print":
      return { state: "in_progress" as const, problem: "at_risk" as const };
    case "held":
    case "checking":
      return { state: "in_progress" as const, problem: null };
  }
}

const TIER_WORDS = {
  cant_print: "Can't print",
  worth_knowing: "Worth knowing",
  we_will_sort: "We'll sort it",
  unrecognised: "Held for our team",
} as const;

/** "A · Bristol", or the line's own number for a line with a single version. */
function versionWords(version: StaffVersionArtwork): string {
  return version.display ?? "the file";
}

export interface QuoteArtworkHandlers {
  busy: boolean;
  /** Open the upload dialog for a version: a first file, or a replacement. */
  onUpload: (versionId: string, replacing: boolean) => void;
  onDownload: (url: string, label: string) => void;
}

function FileRow({
  line,
  version,
  part,
  handlers,
}: {
  line: StaffArtworkLine;
  version: StaffVersionArtwork;
  part: StaffPart;
  handlers: QuoteArtworkHandlers | null;
}) {
  const file = part.file;
  const words = [versionWords(version), part.label].filter(Boolean).join(" ");
  const label = `${line.line_no}${version.label ? ` ${version.label}` : ""}`;
  if (!file) {
    return (
      <RailRow
        {...nodeOfPart(part)}
        label={label}
        name={`No file yet for ${words}`}
        status="Optional until the order"
        actions={
          handlers &&
          part.upload_url && (
            <Button
              size="sm"
              variant="outline"
              disabled={handlers.busy}
              aria-label={`Upload a file for ${line.line_no} ${words}`}
              onClick={() => handlers.onUpload(version.version_id, false)}
            >
              Upload file…
            </Button>
          )
        }
        detail={
          part.supply_sentence && (
            <ViewDetail
              title={`How to supply ${line.line_no} ${words}`}
              description="What the customer is told to send for this part, on their quote page."
            >
              <SupplyGuide
                sentence={part.supply_sentence}
                notes={part.supply_notes}
              />
            </ViewDetail>
          )
        }
      />
    );
  }
  return (
    <RailRow
      {...nodeOfPart(part)}
      label={label}
      name={file.file_name}
      spokenName={`file ${file.file_name}`}
      status={
        <span>
          {part.verdict_sentence}
          <span className="text-muted-foreground">
            {" "}
            · uploaded {short(file.uploaded_at)} by {file.uploaded_by}
            {file.shared_with.length > 0 &&
              ` · shared with ${file.shared_with.join(", ")}`}
          </span>
        </span>
      }
      actions={
        handlers &&
        part.upload_url && (
          <Button
            size="sm"
            variant="outline"
            disabled={handlers.busy}
            aria-label={`Replace the file ${file.file_name}`}
            onClick={() => handlers.onUpload(version.version_id, true)}
          >
            Replace…
          </Button>
        )
      }
      detail={
        <ViewDetail
          title={`${file.file_name} on ${line.line_no} ${words}`}
          description="The automatic check's result, as the customer sees it on their quote page, and what staff see behind it."
        >
          <SupplyGuide
            sentence={part.supply_sentence}
            notes={part.supply_notes}
          />
          {part.findings.length === 0 ? (
            <p>Passed: nothing to tell them.</p>
          ) : (
            <ul className="list-disc space-y-1 pl-5">
              {part.findings.map((f) => (
                <li key={f.id}>
                  <span className="font-medium">{TIER_WORDS[f.tier]}:</span>{" "}
                  {f.sentence ??
                    "Not worded for the customer: they are told we are taking a closer look."}
                  <span className="block text-xs text-muted-foreground">
                    {f.check_name}
                    {f.detail && ` · ${f.detail}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-muted-foreground">
            {file.channel_label} {short(file.uploaded_at)} by {file.uploaded_by}
            . Proofs come once the quote is accepted: the file carries on to the
            order
            {part.verdict === "worth_knowing"
              ? ", and approving its proof needs a tick naming what is worth knowing"
              : ""}
            .
          </p>
          {handlers && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                handlers.onDownload(file.download_url, file.file_name)
              }
            >
              Download original
            </Button>
          )}
        </ViewDetail>
      }
    />
  );
}

/** The rows under one quote line: a file row per part of each version. Nothing for a line that needs no artwork. */
export function QuoteArtworkRows({
  line,
  handlers,
}: {
  line: StaffArtworkLine;
  handlers: QuoteArtworkHandlers | null;
}) {
  if (line.kind !== "artwork") return null;
  return (
    <>
      {line.versions.flatMap((version) =>
        version.parts.map((part) => (
          <FileRow
            key={`${version.version_id}-${part.part_key}`}
            line={line}
            version={version}
            part={part}
            handlers={handlers}
          />
        )),
      )}
    </>
  );
}
